import React, { useRef, useState, useMemo } from 'react';
import { Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { Capacitor } from '@capacitor/core';
import ZoomableImage from '../../../Components/Ui/ZoomableImage';

/** Components Import */
import { BottomSheetActions } from '../../../Reducer/BottomSheetReducer';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import useThrottle from '../../../Hooks/useThrottle';
import PerButton from '../../Permit/ui/PerButton';
import BackClickBottomSheet from '../../Permit/ui/BackClickBottomSheet';

const MAX_PHOTOS = 5;

function M_WOS_BASIC_01(props) {
  const dispatch = useDispatch();
  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const fileInputRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(1);

  const handleTabChange = (stepId) => {
    setCurrentStep(stepId);
  };

  /**
   * 탭(본부/부서/팀/파트)별 선택 가능한 조직 목록 생성
   *
   * masterData: API 조직 목록 (pathArray = [본부, 부서, 팀, 파트], depth = 경로 깊이)
   * selectedTargets: 사용자가 이미 선택한 조직
   *
   * step 1(본부): 전체 조직에서 본부명만 추출
   * step 2~4: 상위 탭에서 선택된 조직의 하위만 표시
   */
  const tabLists = useMemo(() => {
    const getListByStep = (step) => {
      // ── 1. step 2 이상: 표시할 상위 경로 수집 ──
      // validParentPaths = null → step 1, 필터 없음
      // validParentPaths = Set → step 2~4, 해당 경로의 하위만 표시
      let validParentPaths = null;
      if (step > 1) {
        validParentPaths = new Set();
        props.selectedTargets.forEach((t) => {
          if (t.displayDepth === step - 1) {
            // 직접 선택된 상위 조직 (예: 부서 탭에서 "영업부" 선택 → 팀 탭에 영업부 하위만 표시)
            validParentPaths.add(t.selectedPath);
          } else if (t.displayDepth >= step) {
            // 더 깊은 레벨 선택 시 조상 경로도 포함 (상위 탭에서 선택 해제돼도 하위 탭 유지)
            validParentPaths.add(t.pathArray.slice(0, step - 1).join(' > '));
          }
        });
        if (validParentPaths.size === 0) return [];
      }

      const seen = new Set(); // selectedPath 기준 중복 제거
      const list = [];

      // ── 2. masterData 순회 → 해당 step 항목 추출 ──
      (props.masterData || []).forEach((item) => {
        if (!item.pathArray || item.depth < step) return;

        const name = item.pathArray[step - 1]; // 이 탭에 표시할 이름 (step 1→본부명, 2→부서명 …)
        if (!name) return;

        if (validParentPaths) {
          const parentPath = item.pathArray.slice(0, step - 1).join(' > ');
          if (!validParentPaths.has(parentPath)) return;
        }

        const pathArray = item.pathArray.slice(0, step);
        const selectedPath = pathArray.join(' > '); // 선택·비교용 키 (예: "본부A > 부서B")
        if (seen.has(selectedPath)) return;
        seen.add(selectedPath);

        // ── 3. 저장 API용 depCd/depNm: 해당 depth의 정확한 행 조회 ──
        const levelRow = (props.masterData || []).find(
          (r) => r.depth === step && r.pathArray?.join(' > ') === selectedPath
        );

        list.push({
          depCd: levelRow?.depCd ?? levelRow?.DEP_CD,
          depNm:
            levelRow?.depNm ?? levelRow?.depNmLast ?? levelRow?.DEP_NM ?? name,
          depNm1: pathArray[0] ?? '',
          depNm2: pathArray[1] ?? '',
          depNm3: pathArray[2] ?? '',
          depNm4: pathArray[3] ?? '',
          displayName: name,
          selectedPath,
          displayDepth: step,
          pathArray,
        });
      });

      return list.sort((a, b) => a.displayName.localeCompare(b.displayName));
    };

    return {
      1: getListByStep(1),
      2: getListByStep(2),
      3: getListByStep(3),
      4: getListByStep(4),
    };
  }, [props.masterData, props.selectedTargets]);

  const isManager = userInfo.authCd !== 'EMPLOYEE';

  const handleToggleSelect = (item) => {
    const isSelected = props.selectedTargets.some(
      (m) =>
        m.selectedPath === item.selectedPath &&
        m.displayDepth === item.displayDepth
    );

    if (isSelected) {
      props.setSelectedTargets(
        props.selectedTargets.filter(
          (m) =>
            !(
              m.selectedPath === item.selectedPath &&
              m.displayDepth === item.displayDepth
            )
        )
      );
    } else {
      let newList = [...props.selectedTargets];

      newList = newList.filter((m) => {
        const isParent = item.selectedPath.startsWith(m.selectedPath + ' > ');
        const isChild = m.selectedPath.startsWith(item.selectedPath + ' > ');
        return !isParent && !isChild;
      });

      props.setSelectedTargets([...newList, item]);
    }
  };

  const [handleSave, isThrottling] = useThrottle(() => {
    if (isManager && props.selectedTargets.length === 0) {
      dispatch(
        showSnackbar({ message: '대상 조직을 선택하세요.', type: 'warning' })
      );
      return;
    }
    props.SaveLogic1();
  }, 1000);

  return (
    <Box className="flex h-full min-h-0 flex-col overflow-hidden bg-[#f8fafc] font-sans text-[#1e293b]">
      <main className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {/* 현장 사진 및 내용 섹션 */}
        <section className="animate-in fade-in slide-in-from-top duration-700">
          <div className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 p-4">
            <span
              className="material-symbols-outlined fill-1 text-red-500"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              warning
            </span>
            <p className="text-[13px] leading-snug font-medium text-red-700">
              {isManager
                ? '산업재해가 발생할 급박한 위험이 있을 때 작업을 중지시키고 대피 등 필요한 조치를 합니다.'
                : '산업재해가 발생할 급박한 위험이 있을 때 작업을 중지하고 대피하여 관리자에게 보고합니다.'}
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-end justify-between">
            <h2 className="text-[17px] font-bold text-slate-800">현장 사진</h2>
            <span className="text-[11px] font-semibold text-slate-400">
              {
                props.workStopUpload.previewList.filter((img) => !img.deleted)
                  .length
              }{' '}
              / {MAX_PHOTOS}
            </span>
          </div>
          <div className="flex gap-3 overflow-hidden">
            <button
              className="flex aspect-square min-w-22.5 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-slate-200 bg-white transition-transform active:scale-95"
              onClick={() => {
                if (isThrottling) return;
                const count = props.workStopUpload.previewList.filter(
                  (img) => !img.deleted
                ).length;
                if (count >= MAX_PHOTOS) return;
                Capacitor.isNativePlatform()
                  ? props.workStopUpload?.NativeFileChange()
                  : fileInputRef.current?.click();
              }}
            >
              <span className="material-symbols-outlined text-slate-400">
                add_a_photo
              </span>
              <span className="text-2xs font-bold text-slate-400">
                사진 추가
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={props.workStopUpload.FileChange}
            />
            <Swiper slidesPerView="auto" spaceBetween={8} className="flex-1">
              {props.workStopUpload.previewList
                .filter((img) => !img.deleted)
                .map((item, index) => (
                  <SwiperSlide key={index} style={{ width: '90px' }}>
                    <div className="relative aspect-square overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
                      <ZoomableImage
                        src={item.preview}
                        className="h-full w-full object-cover"
                        alt="preview"
                      />
                      <button
                        className="absolute top-1 right-1 rounded-full bg-black/30 p-0.5 text-white"
                        onClick={() => props.workStopUpload.DeletePreview(item)}
                      >
                        <span className="material-symbols-outlined text-sm">
                          close
                        </span>
                      </button>
                    </div>
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-[17px] font-bold text-slate-800">
            내용 <span className="text-xs text-red-500">*</span>
          </h2>
          <textarea
            className="h-32 w-full resize-none rounded-2xl border border-slate-200 bg-white p-4 text-base transition-all outline-none focus:border-[#82B964] focus:ring-2 focus:ring-green-500/20"
            placeholder="작업 중지 사유 및 현장 상황을 상세히 입력해 주세요."
            value={props.workStopInputs.stopComment || ''}
            onChange={(e) =>
              props.setWorkStopInputs({
                ...props.workStopInputs,
                stopComment: e.target.value,
              })
            }
          />
        </section>

        {isManager && (
          <>
            <section className="space-y-2">
              <h2 className="text-lg font-bold text-slate-800">대상 지정</h2>
              <div className="flex items-center gap-1.5 rounded-full bg-green-50 px-3">
                <span className="material-symbols-outlined text-[14px] text-[#82B964]">
                  verified
                </span>
                <span className="text-[11px] font-bold text-[#4a6b3a]">
                  상위 조직 선택 시 하위 부서원이 모두 포함됩니다
                </span>
              </div>
              <div className="rounded-4xl border border-slate-50 bg-white px-4 py-4 shadow-sm">
                {/* 탭 헤더 부분 */}
                <div className="mb-4 flex items-center gap-1.5 rounded-2xl bg-slate-50 p-1">
                  {[
                    { id: 1, n: '본부' },
                    { id: 2, n: '부서' },
                    { id: 3, n: '팀' },
                    { id: 4, n: '파트' },
                  ].map((step) => {
                    const selectedCount = props.selectedTargets.filter(
                      (m) => m.displayDepth === step.id
                    ).length;
                    return (
                      <button
                        key={step.id}
                        onClick={() => handleTabChange(step.id)}
                        className={`relative flex-1 rounded-xl py-2 text-[12px] font-bold transition-all ${currentStep === step.id ? 'bg-[#82B964] text-white shadow-md' : 'text-slate-400'}`}
                      >
                        {step.n}
                        {selectedCount > 0 && (
                          <span
                            className={`absolute -top-1.5 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold ${currentStep === step.id ? 'bg-white text-[#82B964]' : 'bg-[#82B964] text-white'}`}
                          >
                            {selectedCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* 탭 콘텐츠 리스트 부분 */}
                <div className="hide-scrollbar flex h-60 flex-col gap-1.5 space-y-2.5 overflow-y-auto pr-1">
                  {tabLists[currentStep].length === 0 ? (
                    <div className="flex h-full items-center justify-center py-10 text-center text-[13px] text-slate-400">
                      데이터가 없습니다.
                    </div>
                  ) : (
                    tabLists[currentStep].map((item, idx) => {
                      const isSelected = props.selectedTargets.some(
                        (m) =>
                          m.selectedPath === item.selectedPath &&
                          m.displayDepth === item.displayDepth
                      );
                      // 직접 선택되지 않았지만 하위 항목이 선택된 상태
                      const isImplied =
                        !isSelected &&
                        props.selectedTargets.some((m) =>
                          m.selectedPath.startsWith(item.selectedPath + ' > ')
                        );
                      // 하위에 선택된 항목 수
                      const impliedCount = isImplied
                        ? props.selectedTargets.filter((m) =>
                            m.selectedPath.startsWith(item.selectedPath + ' > ')
                          ).length
                        : 0;

                      return (
                        <button
                          key={idx}
                          onClick={() => handleToggleSelect(item)}
                          className={`flex w-full items-center justify-between rounded-2xl border p-4 transition-all active:scale-[0.98] ${
                            isSelected
                              ? 'border-[#82B964] bg-green-50/50'
                              : isImplied
                                ? 'border-dashed border-[#82B964]/60 bg-green-50/20'
                                : 'border-transparent bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center gap-3 text-left">
                            <div
                              className={`flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm ${isSelected || isImplied ? 'text-[#82B964]' : 'text-slate-400'}`}
                            >
                              <span className="material-symbols-outlined">
                                corporate_fare
                              </span>
                            </div>
                            <div>
                              <p
                                className={`text-[14px] font-bold ${isSelected ? 'text-[#2c4e1a]' : isImplied ? 'text-[#4a6b3a]' : 'text-slate-800'}`}
                              >
                                {item.displayName}
                              </p>
                              <p className="text-2xs text-slate-400 uppercase">
                                {isImplied
                                  ? `하위 ${impliedCount}개 선택됨`
                                  : item.pathArray[0]}
                              </p>
                            </div>
                          </div>
                          <div
                            className={`flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors ${
                              isSelected
                                ? 'border-[#82B964] bg-[#82B964]'
                                : isImplied
                                  ? 'border-[#82B964] bg-white'
                                  : 'border-slate-200 bg-white'
                            }`}
                          >
                            {isSelected && (
                              <span className="material-symbols-outlined text-[16px] text-white">
                                check
                              </span>
                            )}
                            {isImplied && (
                              <span className="h-2.5 w-2.5 rounded-full bg-[#82B964]" />
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </section>

            {/* 등록 대상 목록 */}
            <section className="space-y-3">
              <h3 className="px-1 text-[13px] font-bold text-slate-400">
                등록 대상 목록
              </h3>
              <div className="hide-scrollbar h-50 space-y-2.5 overflow-y-auto rounded-2xl border border-dashed border-slate-200 p-2">
                {props.selectedTargets.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-[13px] text-slate-300">
                    선택된 대상이 없습니다.
                  </div>
                ) : (
                  props.selectedTargets.map((member, idx) => (
                    <div
                      key={idx}
                      className="animate-in fade-in zoom-in flex items-center justify-between rounded-2xl border border-slate-100 bg-white p-4 shadow-sm duration-300"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex flex-wrap items-center gap-1">
                          {member.pathArray.map((pathName, pIdx) => (
                            <React.Fragment key={pIdx}>
                              <span className="text-[13px] font-bold text-slate-700">
                                {pathName}
                              </span>
                              {pIdx < member.pathArray.length - 1 && (
                                <span className="material-symbols-outlined text-[12px] text-slate-300">
                                  chevron_right
                                </span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                        <span className="text-2xs rounded-full bg-green-50 px-2.5 py-0.5 font-bold whitespace-nowrap text-[#82B964]">
                          {member.displayDepth === 1
                            ? '본부'
                            : member.displayDepth === 2
                              ? '부서'
                              : member.displayDepth === 3
                                ? '팀'
                                : member.displayDepth === 4
                                  ? '파트'
                                  : `${member.displayDepth}단계`}
                        </span>
                      </div>
                      <button
                        className="text-slate-300 transition-colors hover:text-red-500"
                        onClick={() => handleToggleSelect(member)}
                      >
                        <span className="material-symbols-outlined text-[18px]">
                          close
                        </span>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        )}
      </main>

      {/* 하단 푸터 버튼 및 바텀시트 */}
      <div className="bg-background-light shrink-0 border-t border-slate-100 px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
        <PerButton
          saveText={'저장'}
          disabled={isThrottling}
          onCancel={() =>
            dispatch(
              BottomSheetActions.SetBottomSheetState({
                bottom: true,
                type: 'backClickWos',
              })
            )
          }
          onClick={handleSave}
        />
      </div>

      {bottomSheetState.bottom && bottomSheetState.type === 'backClickWos' && (
        <BackClickBottomSheet open={bottomSheetState.bottom} />
      )}
    </Box>
  );
}

export default M_WOS_BASIC_01;
